import { motionOk } from "./kit";
import { uiUrl } from "./portraits";
import { GraphicsPrefs } from "../render/graphicsPrefs";
import { tierIndex } from "../render/quality";

/**
 * The cloud wipe between title / hub / level / end (ui.md F11; redone after the owner's 09-25 phone play-test: "the
 * cloud screen in between pages needs work"). Two painted cloud banks sweep in from the edges and meet over the
 * middle, the screen swaps underneath, and they part again over the new one. Besides softening the hard DOM cuts it
 * *hides the load hitch*: building a level stalls the main thread for a few hundred ms, which would otherwise be a
 * frozen frame of the old screen.
 *
 * Layers, back to front (art/interface/transition/, painted by art/interface/tools/transition_clouds.py):
 * - a sky wash that fades in as the banks close, so a gap between them never shows the old screen, and fades out
 *   first as they part, so the new screen shows through the opening seam;
 * - the two banks, each ~96 % of the width: closed, their billowing edges overlap over the middle;
 * - three free cumulus puffs in front that travel further and grow more than the banks (parallax), and on the way out
 *   fly past the camera and dissolve.
 * While closed, banks and puffs sway a few pixels, so a long load looks alive rather than frozen; those are
 * compositor animations, so they keep moving while a level build blocks the main thread.
 *
 * Every layer is an <img> or a plain box moved by transform/opacity only; `will-change` is set only while they move,
 * and the overlay is `display:none` in between. It swallows taps while it is up, so a double-tap on PLAY can't start
 * two levels. It is decoration (aria-hidden) and never takes focus; the load panel on it (levelLoading.ts) does.
 *
 * Reduced motion / Reduced effects: no sweep; the closed clouds cross-fade in and out. Low and lowest quality: the
 * banks only (no puffs, no sway).
 */
let root: HTMLDivElement | null = null;
let layers: readonly LayerEl[] = [];
let sky: HTMLElement | null = null;
/** Settles once every sprite is decoded (or failed): a bank that is still blank when it closes shows the old screen. */
let decoded: Promise<unknown> = Promise.resolve();
let active = false;
/** The clouds are parting over the new screen (its buttons are live under them). */
let opening = false;
/** A request made on the new screen while the clouds were still parting: it runs as soon as they are gone. */
let queued: { swap: () => void | Promise<unknown>; opts: WipeOptions } | null = null;

/**
 * How long an ordinary swap (title → hub, a level → the hub) may keep the clouds closed: a stuck load must never leave
 * the screen covered. A level start holds them instead (`hold`), because parting them over the old screen while the
 * level still loads looked exactly like PLAY having failed (owner report, 4G through the tunnel).
 */
export const SwapCapMs = 5000;

/** The sweep in: decelerating, so the banks arrive fast and settle softly rather than stopping dead. */
const CloseMs = 460;
const CloseEase = "cubic-bezier(.2,.7,.3,1)";
/** The parting: a soft start (the new screen has only just painted), then it accelerates away. */
const OpenMs = 560;
const OpenEase = "cubic-bezier(.45,0,.3,1)";
/** The puffs are the nearer layer: they trail the banks in by this much. */
const PuffLagMs = 60;
/** Reduced motion: a quick cross-fade of the closed clouds. */
const FadeInMs = 160;
const FadeOutMs = 240;
/** One sway of the idle drift while closed (it alternates). */
const SwayMs = 2600;
/** The first wipe waits at most this long for its sprites to decode, then goes anyway (the sky wash still covers). */
const DecodeWaitMs = 300;
/** From this quality preset down, the wipe is the banks only. */
const LiteFromTier = "low";
/** Where the sky wash is fully in (as a fraction of the close) and fully out (of the open). */
const SkyInAt = 0.3;
const SkyOutAt = 0.45;
/** The puffs fade in over the start of the close and dissolve over the end of the open. */
const PuffInAt = 0.35;
const PuffOutFrom = 0.45;

const Rest = "translate3d(0,0,0) scale(1)";

interface Layer {
  readonly cls: string;
  readonly src: string;
  readonly puff: boolean;
  /**
   * Where it comes in from and where it leaves to (it rests at `Rest`): transforms, in percent of its own box. A bank
   * leaves just far enough to clear the screen at the very end of the parting, so the overlay (which swallows taps) is
   * never up over a screen that already looks uncovered.
   */
  readonly from: string;
  readonly away: string;
  /** The far end of its idle sway. */
  readonly sway: string;
}

interface LayerEl { readonly layer: Layer; readonly el: HTMLImageElement; url: string }

const LAYERS: readonly Layer[] = [
  { cls: "bank l", src: "bank_l.webp", puff: false, from: "translate3d(-104%,0,0) scale(1.06)", away: "translate3d(-80%,-3%,0) scale(1.1)", sway: "translate3d(1.2%,0.4%,0) scale(1.01)" },
  { cls: "bank r", src: "bank_r.webp", puff: false, from: "translate3d(104%,0,0) scale(1.06)", away: "translate3d(80%,3%,0) scale(1.1)", sway: "translate3d(-1.2%,-0.4%,0) scale(1.01)" },
  { cls: "puff p1", src: "puff_1.webp", puff: true, from: "translate3d(-140%,-10%,0) scale(1.25)", away: "translate3d(-150%,-25%,0) scale(1.5)", sway: "translate3d(4%,-2%,0) scale(1.02)" },
  { cls: "puff p2", src: "puff_2.webp", puff: true, from: "translate3d(140%,6%,0) scale(1.25)", away: "translate3d(150%,12%,0) scale(1.5)", sway: "translate3d(-4%,2%,0) scale(1.02)" },
  { cls: "puff p3", src: "puff_3.webp", puff: true, from: "translate3d(-140%,10%,0) scale(1.25)", away: "translate3d(-150%,25%,0) scale(1.5)", sway: "translate3d(5%,1%,0) scale(1.02)" },
];

export interface WipeOptions {
  /**
   * Keep the clouds closed until `swap` settles, however long that takes. Only for a swap that always settles on its
   * own and shows the player why it takes a while: a level load, whose progress panel (levelLoading.ts) offers CANCEL.
   */
  readonly hold?: boolean;
}

const spriteUrl = (l: Layer) => uiUrl(`transition/${l.src}`);
const decodeAll = () => Promise.all(layers.map((l) => (typeof l.el.decode === "function" ? l.el.decode().catch(() => undefined) : undefined)));

function overlay(): HTMLDivElement {
  if (root) return root;
  const el = document.createElement("div");
  el.className = "wipe hidden";
  el.setAttribute("aria-hidden", "true");
  const img = (l: Layer) => `<img class="${l.cls}" decoding="async" draggable="false" src="${spriteUrl(l)}" alt="">`;
  el.innerHTML = `<div class="sky"></div>${LAYERS.map(img).join("")}`;
  sky = el.querySelector<HTMLElement>(".sky");
  layers = LAYERS.map((layer) => ({ layer, el: el.querySelector<HTMLImageElement>(`.${layer.cls.replace(" ", ".")}`)!, url: spriteUrl(layer) }));
  decoded = decodeAll();
  document.body.append(el);
  root = el;
  return el;
}

/**
 * A single-file build (tools/web/singlefile.mjs) fills the inlined asset store only once the save is open, after the
 * prewarm below has run: point the sprites at their inlined copies before they are first shown.
 */
function refreshSprites(): void {
  let changed = false;
  for (const l of layers) {
    const url = spriteUrl(l.layer);
    if (url !== l.url) { l.url = url; l.el.src = url; changed = true; }
  }
  if (changed) decoded = decodeAll();
}

// Fetch and decode the sprites as soon as the page is up, so the first wipe (TAP TO START) has them ready.
if (typeof window !== "undefined" && typeof document !== "undefined") setTimeout(() => void overlay(), 0);

const nextFrame = () => new Promise<void>((res) => requestAnimationFrame(() => res()));
const wait = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));
const settled = (a: Animation | undefined) => (a ? a.finished.then(() => undefined, () => undefined) : Promise.resolve());

/** Low and lowest presets: the banks only. */
function liteTier(): boolean { return GraphicsPrefs.presetIndex() >= tierIndex(LiteFromTier); }

/** The banks (and the puffs, unless lite) sweep in while the sky wash fades up behind them. */
async function close(moving: readonly LayerEl[]): Promise<void> {
  sky?.animate([{ opacity: 0 }, { opacity: 0, offset: SkyInAt }, { opacity: 1 }], { duration: CloseMs, fill: "forwards" });
  await Promise.all(moving.map(({ layer, el }) => settled(el.animate(
    layer.puff ? [{ transform: layer.from, opacity: 0 }, { opacity: 1, offset: PuffInAt }, { transform: Rest, opacity: 1 }] : [{ transform: layer.from }, { transform: Rest }],
    { duration: CloseMs, delay: layer.puff ? PuffLagMs : 0, easing: CloseEase, fill: "both" },
  ))));
}

/** While closed each layer sways a little, until the clouds part. */
function sway(moving: readonly LayerEl[]): void {
  for (const { layer, el } of moving) el.animate([{ transform: Rest }, { transform: layer.sway }], { duration: SwayMs, easing: "ease-in-out", direction: "alternate", iterations: Infinity });
}

/** The sky wash goes first, so the new screen shows through the seam; banks and puffs part from where their sway is. */
async function part(moving: readonly LayerEl[]): Promise<void> {
  sky?.animate([{ opacity: 1 }, { opacity: 0, offset: SkyOutAt }, { opacity: 0 }], { duration: OpenMs, easing: "ease-out", fill: "forwards" });
  await Promise.all(moving.map(({ layer, el }) => {
    const now = typeof getComputedStyle === "function" ? getComputedStyle(el).transform : "";
    const at = now && now !== "none" ? now : Rest;
    el.getAnimations().forEach((a) => a.cancel());
    return settled(el.animate(
      layer.puff ? [{ transform: at, opacity: 1 }, { opacity: 1, offset: PuffOutFrom }, { transform: layer.away, opacity: 0 }] : [{ transform: at }, { transform: layer.away }],
      { duration: OpenMs, easing: OpenEase, fill: "forwards" },
    ));
  }));
}

/** Reduced motion: the closed clouds fade in or out as one. */
function fade(el: HTMLElement, from: number, to: number, ms: number): Promise<void> {
  if (sky) sky.style.opacity = "1";
  return settled(el.animate([{ opacity: from }, { opacity: to }], { duration: ms, easing: "ease-out", fill: "forwards" }));
}

/**
 * Runs `swap` under the clouds. `swap` may return a promise (level loading); the clouds stay closed until it settles
 * (capped at `SwapCapMs` unless `opts.hold`), then two frames more so the new screen has painted.
 * A wipe requested while the clouds are closing or the screen is swapping is dropped: that is a second tap on TAP TO
 * START / PLAY / an end-screen button landing before the clouds closed, and running it would build the next screen
 * twice. One requested while they are PARTING is a tap on the new screen (its PLAY is already live under the clouds),
 * and it runs as soon as they are gone. It used to be dropped too: PLAY tapped in the ~0.4 s the title's clouds took
 * to part did nothing, and a capture that taps it once (the `?webp` run, whose slower frames stretch that window)
 * waited forever for a level that was never asked for.
 */
export async function cloudWipe(swap: () => void | Promise<unknown>, opts: WipeOptions = {}): Promise<void> {
  if (active) {
    if (opening && queued === null) queued = { swap, opts };
    return;
  }
  if (typeof document.body.animate !== "function") { await swap(); return; }
  active = true;
  const el = overlay();
  refreshSprites();
  const motion = motionOk();
  const lite = !motion || liteTier();
  const moving = layers.filter(({ layer }) => !(lite && layer.puff));
  try {
    await Promise.race([decoded, wait(DecodeWaitMs)]);
    if (lite) el.classList.add("lite"); else el.classList.remove("lite");
    for (const { el: x } of moving) x.style.willChange = "transform, opacity";
    el.classList.remove("hidden");
    if (motion) await close(moving); else await fade(el, 0, 1, FadeInMs);
    if (motion && !lite) sway(moving);
    try {
      const swapped = Promise.resolve(swap());
      await (opts.hold ? swapped : Promise.race([swapped, wait(SwapCapMs)]));
    } catch (err) { console.warn("screen swap failed", err); }
    opening = true;
    await nextFrame(); await nextFrame();
    if (motion) await part(moving); else await fade(el, 1, 0, FadeOutMs);
  } finally {
    for (const x of [el, sky, ...layers.map((l) => l.el)]) x?.getAnimations().forEach((a) => a.cancel());
    for (const { el: x } of moving) x.style.willChange = "";
    if (sky) sky.style.opacity = "";
    el.classList.add("hidden");
    active = false;
    opening = false;
  }
  const next = queued;
  queued = null;
  if (next) await cloudWipe(next.swap, next.opts);
}

/** True while the clouds are closed or moving (screens can hold their entrance animations until the reveal). */
export function wipeActive(): boolean { return active; }
